import { useState, useEffect } from "react";
import { API, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { BarChart3, Calendar, TrendingUp, Package, IndianRupee, Search, ChevronDown, ChevronUp, FileText, FileSpreadsheet } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReports() {
  const { symbol } = useCurrency();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [specificDate, setSpecificDate] = useState("");
  const [searchMode, setSearchMode] = useState("range");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setSpecificDate(today);
    fetchReport(null, null, today);
  }, []);

  const fetchReport = async (from, to, specific) => {
    setLoading(true);
    try {
      let url = `${API}/admin/reports/daily-sales?`;
      if (specific) url += `specific_date=${specific}`;
      else {
        if (from) url += `date_from=${from}&`;
        if (to) url += `date_to=${to}`;
      }
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) setReport(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSearch = () => {
    if (searchMode === "specific") fetchReport(null, null, specificDate);
    else fetchReport(dateFrom, dateTo, null);
  };

  const toggleDay = (date) => setExpandedDays((prev) => ({ ...prev, [date]: !prev[date] }));

  const getExportParams = () => {
    if (searchMode === "specific") return `specific_date=${specificDate}`;
    let p = "";
    if (dateFrom) p += `date_from=${dateFrom}&`;
    if (dateTo) p += `date_to=${dateTo}`;
    return p;
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-reports">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Sales Report</h1>
          </div>
          <div className="flex gap-1.5 sm:gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/reports/export-pdf?${getExportParams()}`, "_blank")}
              className="text-red-600 border-red-200 hover:bg-red-50 text-xs px-2 sm:px-3"
              data-testid="export-pdf-btn"
            >
              <FileText size={13} className="mr-1" /> PDF
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => window.open(`${BACKEND_URL}/api/admin/reports/export-excel?${getExportParams()}`, "_blank")}
              className="text-green-600 border-green-200 hover:bg-green-50 text-xs px-2 sm:px-3"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet size={13} className="mr-1" /> Excel
            </Button>
          </div>
        </div>

        {/* Search Filters */}
        <div className="bg-white rounded-xl border p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSearchMode("specific")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${searchMode === "specific" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600"}`}
              data-testid="mode-specific"
            >
              Specific Date
            </button>
            <button
              onClick={() => setSearchMode("range")}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg ${searchMode === "range" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600"}`}
              data-testid="mode-range"
            >
              Date Range
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            {searchMode === "specific" ? (
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">Date</label>
                <Input type="date" value={specificDate} onChange={(e) => setSpecificDate(e.target.value)} className="text-sm" data-testid="specific-date" />
              </div>
            ) : (
              <div className="flex gap-2 flex-1">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">From</label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="text-sm" data-testid="date-from" />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">To</label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="text-sm" data-testid="date-to" />
                </div>
              </div>
            )}
            <Button onClick={handleSearch} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white text-sm w-full sm:w-auto" data-testid="search-btn">
              <Search size={14} className="mr-1" /> Search
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {report && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 mb-4 sm:mb-6">
              <SummaryCard icon={<Package size={16} />} label="Orders" value={report.summary.total_orders} color="#3B82F6" />
              <SummaryCard icon={<TrendingUp size={16} />} label="Revenue" value={`${symbol}${Math.round(report.summary.total_revenue)}`} color="#10B981" />
              <SummaryCard icon={<IndianRupee size={16} />} label="Tax" value={`${symbol}${Math.round(report.summary.total_tax)}`} color="#F59E0B" />
              <SummaryCard icon={<IndianRupee size={16} />} label="Subtotal" value={`${symbol}${Math.round(report.summary.total_subtotal)}`} color="#8B5CF6" />
            </div>

            {/* Daily Breakdown */}
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="p-3 sm:p-4 border-b">
                <h2 className="text-sm font-bold text-gray-900">Daily Breakdown</h2>
              </div>
              {report.daily_breakdown.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">No orders found</div>
              ) : (
                report.daily_breakdown.map((day) => (
                  <div key={day.date} className="border-b last:border-0">
                    <button
                      onClick={() => toggleDay(day.date)}
                      className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`day-${day.date}`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                        <div className="text-left">
                          <p className="text-xs sm:text-sm font-semibold text-gray-900">{new Date(day.date + "T00:00:00").toLocaleDateString("en-US", {weekday: "short", month: "short", day: "numeric"})}</p>
                          <p className="text-[10px] text-gray-500">{day.order_count} orders</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-green-600">{symbol}{Math.round(day.total_revenue)}</span>
                        {expandedDays[day.date] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </button>
                    {expandedDays[day.date] && (
                      <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                        {/* Desktop table */}
                        <div className="hidden md:block">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 border-b">
                                <th className="pb-2 text-left font-medium">Order #</th>
                                <th className="pb-2 text-left font-medium">Customer</th>
                                <th className="pb-2 text-left font-medium">Email</th>
                                <th className="pb-2 text-left font-medium">Segments</th>
                                <th className="pb-2 text-right font-medium">Subtotal</th>
                                <th className="pb-2 text-right font-medium">Tax</th>
                                <th className="pb-2 text-right font-medium">Total</th>
                                <th className="pb-2 text-center font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {day.orders.map((order) => (
                                <tr key={order.order_id} className="border-b last:border-0">
                                  <td className="py-2 font-semibold text-green-600">{order.order_number || order.order_id.slice(0, 12)}</td>
                                  <td className="py-2 text-gray-700">
                                    <div className="flex flex-col">
                                      <span>{order.user_name}</span>
                                      {order.user_phone && <span className="text-[10px] text-gray-400">{order.user_phone}</span>}
                                    </div>
                                  </td>
                                  <td className="py-2 text-gray-500 break-all max-w-[180px]">{order.user_email || "-"}</td>
                                  <td className="py-2">
                                    {order.customer_segments?.length ? (
                                      <div className="flex flex-wrap gap-1 max-w-[180px]">
                                        {order.customer_segments.map((s) => (
                                          <span key={s} className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100">
                                            {s}
                                          </span>
                                        ))}
                                      </div>
                                    ) : <span className="text-gray-300">-</span>}
                                  </td>
                                  <td className="py-2 text-right text-gray-700">{symbol}{Math.round(order.subtotal)}</td>
                                  <td className="py-2 text-right text-gray-500">{symbol}{Math.round(order.total_tax)}</td>
                                  <td className="py-2 text-right font-bold text-gray-900">{symbol}{Math.round(order.total_amount)}</td>
                                  <td className="py-2 text-center">
                                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                                      order.status === "delivered" ? "bg-green-100 text-green-700" :
                                      order.status === "cancelled" ? "bg-red-100 text-red-700" :
                                      "bg-yellow-100 text-yellow-700"
                                    }`}>{order.status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {/* Mobile cards */}
                        <div className="md:hidden space-y-2">
                          {day.orders.map((order) => (
                            <div key={order.order_id} className="bg-gray-50 rounded-lg p-2.5">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-green-600">{order.order_number || order.order_id.slice(0, 10)}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                                  order.status === "delivered" ? "bg-green-100 text-green-700" :
                                  order.status === "cancelled" ? "bg-red-100 text-red-700" :
                                  "bg-yellow-100 text-yellow-700"
                                }`}>{order.status}</span>
                              </div>
                              <p className="text-xs text-gray-700 font-medium">{order.user_name}</p>
                              {order.user_email && <p className="text-[10px] text-gray-500 break-all">{order.user_email}</p>}
                              {order.customer_segments?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {order.customer_segments.map((s) => (
                                    <span key={s} className="inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100">
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-[10px] text-gray-500">Tax: {symbol}{Math.round(order.total_tax)}</span>
                                <span className="text-sm font-bold text-gray-900">{symbol}{Math.round(order.total_amount)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between pt-2.5 mt-2 border-t text-xs">
                          <span className="text-gray-500">Day Total</span>
                          <div className="text-right">
                            <span className="text-gray-500 mr-3">Tax: {symbol}{Math.round(day.total_tax)}</span>
                            <span className="font-bold text-green-600">{symbol}{Math.round(day.total_revenue)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border p-3 sm:p-4" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 mb-1 sm:mb-2">
        <div style={{color}}>{icon}</div>
        <span className="text-[10px] text-gray-500 font-medium">{label}</span>
      </div>
      <p className="text-base sm:text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}
